import { Static, Type } from '@sinclair/typebox';
import {
  PublicCategorySchema,
  PublicPlaceSchema,
  PublicPrestationItemSchema,
  PublicPriceOptionSchema,
  PublicTeamMemberSchema,
} from './public-professional';
import { AddressSchema } from './professional';
import { SupplementQuantitiesSchema } from './catalog-selection';
import { BookableAvailabilityItemSchema, IncludeSessionSlotsBelowPartySizeSchema } from './availabilities';
import { ProResourceCategoryEnum } from './pro_resource/pro_resource.schema';
import { BookingCustomFieldPublicSchema } from './booking_custom_field';
import { BookingPromoIneligibilityReason } from './pro_promo_code';
import { PromotionPendingCondition } from './promotion-pricing';
import { PrestationPromotionFields } from './promotion-public';
import { ProPromoCodeCustomerScope } from './translation_namespace/pro_promo_code_namespace';
import { BookingBookabilityReason } from './translation_namespace/booking_bookability_reason_namespace';
import { BookingAmountsSchema } from './booking_amounts';
import { PrestationPaymentType } from './translation_namespace/prestation_namespace';

export const BookingSelectableResourceSchema = Type.Object({
  pro_resource_id: Type.Number(),
  name: Type.String(),
  category: ProResourceCategoryEnum,
  description: Type.Optional(Type.String()),
  pax_min: Type.Optional(Type.Number()),
  pax_max: Type.Optional(Type.Number()),
  small_luggage_min: Type.Optional(Type.Number()),
  small_luggage_max: Type.Optional(Type.Number()),
  large_luggage_min: Type.Optional(Type.Number()),
  large_luggage_max: Type.Optional(Type.Number()),
  pro_place_ids: Type.Array(Type.Number()),
});
export type BookingSelectableResourceObject = Static<typeof BookingSelectableResourceSchema>;

// ─── Booking Tunnel: Prestations ───

export const BookingPrestationsSlotSchema = Type.Object({
  prestation_id: Type.Number(),
  duration: Type.Optional(Type.Integer({ minimum: 1 })),
  team_member_id: Type.Optional(Type.Number()),
  // Quantité de l'item au panier (party_size). Impacte la capacité ressource/session, donc la
  // faisabilité d'enchaînement. Absent ⇒ 1.
  quantity: Type.Optional(Type.Integer({ minimum: 1 })),
  selected_supplement_ids: Type.Optional(Type.Array(Type.Integer())),
});

export const BookingPrestationsRequestSchema = Type.Object({
  pro_place_id: Type.Optional(Type.Number()),
  slots: Type.Optional(Type.Array(BookingPrestationsSlotSchema)),
  tolerance_minutes: Type.Optional(Type.Integer({ minimum: 0 })),
  /* Tunnel scoping filters (set by the BookingWidget / share link) */
  prestation_ids: Type.Optional(Type.Array(Type.Number())),
  category_ids: Type.Optional(Type.Array(Type.Number())),
  place_ids: Type.Optional(Type.Array(Type.Number())),
  member_ids: Type.Optional(Type.Array(Type.Number())),
});
export type BookingPrestationsRequestObject = Static<typeof BookingPrestationsRequestSchema>;

export const BookableAvailabilityPublicRequestSchema = Type.Object({
  start_date: Type.Optional(Type.String({ format: 'date-time' })),
  slots: Type.Array(BookableAvailabilityItemSchema, { minItems: 1 }),
  tolerance_minutes: Type.Optional(Type.Integer({ minimum: 0 })),
  include_session_slots_below_party_size: IncludeSessionSlotsBelowPartySizeSchema,
});
export type BookableAvailabilityPublicRequestObject = Static<typeof BookableAvailabilityPublicRequestSchema>;

/**
 * Option de prix (= variante durée/prix) enrichie de sa RÉSERVABILITÉ propre. Spécifique au tunnel :
 * le catalogue public (`PublicPriceOptionSchema`) ne calcule aucune disponibilité, ces champs y
 * seraient toujours absents.
 *
 * ⚠️ L'ordre et la longueur du tableau `prices` ne doivent JAMAIS varier selon la disponibilité :
 * `selected_price_index` est un index dans ce tableau, persisté et relu par le panier, le checkout,
 * la tarification et la revalidation serveur de la pré-réservation. Le masquage d'une durée est une
 * décision d'AFFICHAGE, portée par ce drapeau — cf. `isAvailablePriceOption`.
 */
export const BookingPriceOptionSchema = Type.Object({
  ...PublicPriceOptionSchema.properties,
  /*
   * `false` ⇒ durée sondée et sans aucun créneau (typiquement « trop longue pour tenir dans une
   * fenêtre d'ouverture ») → le tunnel la masque. ABSENT ⇒ durée NON évaluée (sonde plafonnée) →
   * on la laisse visible : jamais de masquage sans preuve.
   */
  is_bookable: Type.Optional(Type.Boolean()),
  /*
   * Jour mural du pro (`YYYY-MM-DD`) du 1er créneau trouvé POUR CETTE DURÉE. Même nature que
   * `first_bookable_date` de la prestation : valeur MACHINE (ouvre la grille hebdo sur la bonne
   * semaine), jamais affichée — la règle « dates formatées par le backend » ne s'applique pas.
   * Renseigné uniquement sur les durées réellement sondées et réservables.
   */
  first_bookable_date: Type.Optional(Type.String()),
});
export type BookingPriceOptionObject = Static<typeof BookingPriceOptionSchema>;

/**
 * Promotion automatique d'une carte du tunnel (la plus avantageuse qui vise la prestation), sous-ensemble
 * de la promotion d'une prestation du site vitrine (`PrestationPromotionFields`).
 */
export const BookingCardPromotionSchema = Type.Object(PrestationPromotionFields);
export type BookingCardPromotionObject = Static<typeof BookingCardPromotionSchema>;

export const BookingPrestationItemSchema = Type.Object({
  ...PublicPrestationItemSchema.properties,
  // Surcharge du `prices` public : mêmes lignes, même ordre, mais porteuses de leur réservabilité.
  prices: Type.Optional(Type.Array(BookingPriceOptionSchema)),
  is_bookable: Type.Boolean(),
  // P3 : pourquoi la prestation n'est pas réservable (présent seulement si `is_bookable` est faux), déduit de
  // la configuration et de la sonde existante. Le tunnel en tire une phrase et une sortie.
  bookability_reason: Type.Optional(Type.Enum(BookingBookabilityReason)),
  /*
   * `false` ⟺ la prestation est en mode « Aucun paiement requis » (FREE) : rien n'est encaissé ni
   * empreinté en ligne. Pilote le bandeau « Paiement sécurisé » du tunnel, affiché uniquement quand
   * le panier contient au moins une ligne payable en ligne. Absent (ancien backend) ⇒ payable.
   */
  requires_online_payment: Type.Optional(Type.Boolean()),
  /*
   * Jour mural du pro (`YYYY-MM-DD`) du 1er créneau réservable trouvé par la sonde `is_bookable`.
   * Valeur MACHINE, jamais affichée : elle sert au tunnel à ouvrir la grille hebdo sur la bonne
   * semaine. Sans elle, une prestation dont la 1re dispo est au-delà des 7 jours scannés par le
   * listing afficherait une grille vide. La règle « dates formatées par le backend » ne s'applique
   * donc pas ici. Absente quand la prestation n'est pas réservable.
   *
   * Ancrée sur la durée réservable la PLUS COURTE (donc la plus précoce) : c'est le repli des
   * durées non sondées. Une durée sondée porte sa propre date sur `prices[].first_bookable_date`,
   * que le tunnel préfère quand elle existe — ouvrir la grille trop tôt coûte une navigation,
   * l'ouvrir trop tard masque des créneaux.
   */
  first_bookable_date: Type.Optional(Type.String()),
  // Même jour, formaté serveur (« 18 nov. 2026 ») : « Réservable à partir du … » quand il dépasse la
  // première semaine de la grille (délai minimum du pro, spec Parcours § 7.8 TOO_SHORT_NOTICE).
  first_bookable_date_formatted: Type.Optional(Type.String()),
  team_member_ids: Type.Optional(Type.Array(Type.Number())),
  // Lieux (pro_place) où la prestation est proposée. Convention identique à team_member_ids :
  // liste vide/absente ⇒ « tous lieux » (aucune contrainte). Sert au tunnel à griser les lieux
  // incompatibles avec le panier sans aucun appel réseau supplémentaire.
  pro_place_ids: Type.Optional(Type.Array(Type.Number())),
  selectable_resources: Type.Optional(Type.Array(BookingSelectableResourceSchema)),
  required_team_members_count: Type.Optional(Type.Integer({ minimum: 0 })),
  is_session: Type.Optional(Type.Boolean()),
  is_party_size_open: Type.Optional(Type.Boolean()),
  clients_per_booking_max: Type.Optional(Type.Integer({ minimum: 1 })),
  max_session_capacity: Type.Optional(Type.Integer({ minimum: 1 })),
  // ── Carte « Course » (type RIDE — `type` est porté par PublicPrestationItemSchema) ──
  // La carte ne se réserve PAS dans le tunnel classique : son CTA bascule vers le flux course
  // avec le trajet par défaut prérempli. Une adresse « locked » est en lecture seule côté client.
  // is_bookable d'une carte RIDE = véhicule actif + tarif (règle du pool), jamais la sonde créneaux.
  ride_default_pickup_address: Type.Optional(AddressSchema),
  ride_default_dropoff_address: Type.Optional(AddressSchema),
  ride_pickup_address_locked: Type.Optional(Type.Boolean()),
  ride_dropoff_address_locked: Type.Optional(Type.Boolean()),
  // Estimation « à partir de » (cache posé à la sauvegarde de la prestation, trajet par défaut
  // complet uniquement) — pré-formatée backend (règle prix). Absente : « Prix selon trajet ».
  ride_estimated_price: Type.Optional(
    Type.String({ meta: { is_price: true, source_key: 'ride_estimated_price_minor' } }),
  ),
  // Promotion automatique qui vise la prestation (badge, prix barré si acquise).
  promotion: Type.Optional(BookingCardPromotionSchema),
});
export type BookingPrestationItemObject = Static<typeof BookingPrestationItemSchema>;

// ─── Booking Tunnel: Team Members ───

export const BookingTeamMemberSchema = Type.Object({
  ...PublicTeamMemberSchema.properties,
  // Lieux (pro_place) où le membre peut intervenir, dérivés des availabilities (un calendrier lie
  // ensemble membres + lieux). Convention identique à `pro_place_ids` des prestations : liste
  // vide/absente ⇒ « tous lieux » (aucune contrainte). Sert au tunnel à griser les membres
  // incompatibles avec le lieu sélectionné sans appel réseau supplémentaire.
  pro_place_ids: Type.Optional(Type.Array(Type.Number())),
});
export type BookingTeamMemberObject = Static<typeof BookingTeamMemberSchema>;

/* Public booking-tunnel behavior flags (subset of pro_booking_params) */
export const BookingPublicParamsSchema = Type.Object({
  allow_multi_prestation: Type.Optional(Type.Boolean()),
  allow_multi_member: Type.Optional(Type.Boolean()),
});
export type BookingPublicParamsObject = Static<typeof BookingPublicParamsSchema>;

export const BookingPrestationsResponseSchema = Type.Object({
  categories: Type.Array(PublicCategorySchema),
  prestations: Type.Array(BookingPrestationItemSchema),
  team_members: Type.Array(BookingTeamMemberSchema),
  pro_places: Type.Array(PublicPlaceSchema),
  custom_fields: Type.Array(BookingCustomFieldPublicSchema),
  booking_params: Type.Optional(BookingPublicParamsSchema),
  // Tokens Stripe des moyens de paiement actifs sur le compte Connect du pro (affichage de réassurance
  // dans le tunnel). Vide/absent si le compte ne peut pas encaisser. Le checkout réel reste card-only.
  allowed_payment_methods: Type.Optional(Type.Array(Type.String())),
});
export type BookingPrestationsResponseObject = Static<typeof BookingPrestationsResponseSchema>;

// ─── Booking Tunnel: Bookability (recalcul live léger) ───

/**
 * Endpoint léger dédié au recalcul de `is_bookable` lorsqu'un panier multi-prestation évolue.
 * Ne renvoie qu'une liste de booléens (pas le payload prestations complet), afin de garder
 * l'appel — déjà gaté côté front (multi-prestation + panier non vide) — le plus bon marché possible.
 */
export const BookingBookabilityRequestSchema = Type.Object({
  // Panier courant (prestations déjà sélectionnées), contexte du calcul de conflit temporel.
  slots: Type.Array(BookingPrestationsSlotSchema),
  // Prestations candidates à évaluer (non encore au panier). Borne le coût CPU backend.
  candidate_prestation_ids: Type.Optional(Type.Array(Type.Number())),
  pro_place_id: Type.Optional(Type.Number()),
  tolerance_minutes: Type.Optional(Type.Integer({ minimum: 0 })),
});
export type BookingBookabilityRequestObject = Static<typeof BookingBookabilityRequestSchema>;

export const BookingBookabilityResponseSchema = Type.Object({
  prestations: Type.Array(
    Type.Object({
      prestation_id: Type.Number(),
      is_bookable: Type.Boolean(),
      /*
       * Réservabilité PAR DURÉE, recalculée avec le panier courant (mêmes règles que
       * `prices[].is_bookable` du listing : une durée absente de la liste n'a pas été sondée et ne
       * doit pas être masquée). Le tunnel la fusionne dans `prices` par `duration_minutes` — et non
       * par index — car cet endpoint léger ne renvoie pas le catalogue.
       */
      durations: Type.Optional(
        Type.Array(
          Type.Object({
            duration_minutes: Type.Integer({ minimum: 1 }),
            is_bookable: Type.Boolean(),
          }),
        ),
      ),
      // 1re date réservable (`YYYY-MM-DD`, jour mural du pro), re-sondée après le choix du lieu :
      // le front ouvre la grille sur cette semaine au lieu de celle de la sonde initiale
      // (faite hors lieu/variante, donc potentiellement trop lointaine — audit P2 #6).
      first_bookable_date: Type.Optional(Type.String()),
    }),
  ),
});
export type BookingBookabilityResponseObject = Static<typeof BookingBookabilityResponseSchema>;

// ─── Booking Tunnel: Eligible members per chain position ───

/**
 * Éligibilité membre par position du panier (étape « choisir par membre », multi-prestation).
 * Pour chaque position laissée « sans préférence », renvoie les membres pouvant la servir dans une
 * chaîne réservable, conditionné par les membres déjà choisis sur les autres positions. Le tunnel
 * grise en direct les membres absents de cet ensemble (combinaison sans créneau enchaînable).
 */
export const BookingEligibleMembersSlotSchema = Type.Object({
  prestation_id: Type.Number(),
  pro_place_id: Type.Number(),
  duration: Type.Integer({ minimum: 1 }),
  quantity: Type.Optional(Type.Integer({ minimum: 1 })),
  team_member_id: Type.Optional(Type.Number()),
});

export const BookingEligibleMembersRequestSchema = Type.Object({
  slots: Type.Array(BookingEligibleMembersSlotSchema, { minItems: 1 }),
  tolerance_minutes: Type.Optional(Type.Integer({ minimum: 0 })),
});
export type BookingEligibleMembersRequestObject = Static<typeof BookingEligibleMembersRequestSchema>;

export const BookingEligibleMembersResponseSchema = Type.Object({
  positions: Type.Array(
    Type.Object({
      index: Type.Number(),
      eligible_member_ids: Type.Array(Type.Number()),
      unconstrained: Type.Boolean(),
    }),
  ),
});
export type BookingEligibleMembersResponseObject = Static<typeof BookingEligibleMembersResponseSchema>;

// ─── Booking Tunnel: Cart Summary ───

export const CartSummaryItemSchema = Type.Object({
  prestation_id: Type.Number(),
  line_label: Type.String(),
  line_price: Type.String(),
  /* Décomposition du prix de ligne quand plusieurs unités sont facturées (taille de groupe et/ou
     quantité) : `line_price` est le TOTAL de la ligne, ces deux champs disent d'où il vient.
     Renseignés seulement au-delà d'une unité — l'UI n'affiche « 4 × 20,00 € » que si ça éclaire. */
  line_units: Type.Optional(Type.Integer({ minimum: 2 })),
  /** Prix d'UNE unité, déjà formaté serveur (jamais recalculé côté client). */
  line_unit_price: Type.Optional(Type.String()),
  /* Pricing mode of the selected option (FIXED | FROM | QUOTE) — lets the UI show "Sur devis" / "À partir de" instead of a raw 0 €. */
  price_type: Type.Optional(Type.String()),
  /** Formule choisie (libellé du pro), seulement quand la prestation en propose plusieurs (spec Parcours § 9). */
  formula: Type.Optional(Type.String()),
  /** Durée de la ligne (formule et suppléments, × quantité), au format du tunnel : « 1h30 », « 45 min ». */
  duration: Type.Optional(Type.String()),
  supplements: Type.Array(
    Type.Object({
      name: Type.String(),
      /** Prix total du supplément sur la ligne (prix unitaire × quantité), formaté serveur. */
      price: Type.String(),
      /** Nombre de ce supplément sur la ligne, renseigné au-delà d'un seul. */
      quantity: Type.Optional(Type.Integer({ minimum: 2 })),
    }),
  ),
  /** Offre qui remise la ligne (badge du récap) : la ligne garde son prix normal, la remise a sa propre ligne. */
  discount_label: Type.Optional(Type.String()),
});

export type CartSummaryItemObject = Static<typeof CartSummaryItemSchema>;

export const CartSummaryRequestSchema = Type.Object({
  items: Type.Array(
    Type.Object({
      prestation_id: Type.Number(),
      quantity: Type.Integer({ minimum: 1 }),
      selected_price_index: Type.Integer({ minimum: 0 }),
      // Identifiant stable de la formule : prime sur l'index quand elle existe encore.
      price_id: Type.Optional(Type.Integer({ minimum: 1 })),
      selected_supplement_ids: Type.Optional(Type.Array(Type.Integer())),
      supplement_quantities: Type.Optional(SupplementQuantitiesSchema),
      party_size: Type.Optional(Type.Integer({ minimum: 1 })),
      // Créneau et membres de l'article, quand ils sont choisis (lot 4 : une promotion juge chaque ligne
      // sur les siens) ; à défaut, `slot_start` de la réservation.
      slot_start: Type.Optional(Type.String({ format: 'date-time' })),
      team_member_ids: Type.Optional(Type.Array(Type.Integer())),
    }),
    { minItems: 1 },
  ),
  // Même borne que `promo_code` : le champ unique du tunnel essaie d'abord le code saisi comme carte
  // cadeau, et une borne plus courte rejetait un code promo long en validation, sans essai du promo.
  voucher_code: Type.Optional(Type.String({ maxLength: 64 })),
  promo_code: Type.Optional(Type.String({ maxLength: 64 })),
  // Code saisi dans le champ unique « Code promo ou carte cadeau » : un code promo du pro s'il en est un,
  // sinon une carte cadeau ou un bon. Il remplace le code de même nature (`promo_code` ou `voucher_code`) ;
  // la réponse dit lequel a été retenu.
  code: Type.Optional(Type.String({ maxLength: 64 })),
  // Contexte connu de l'étape paiement AVANT la pré-réservation : début du (premier) créneau choisi et
  // email saisi. Avec eux, le code promo est tranché tout de suite (happy hours, code personnel,
  // nouveau client, plafond par client) au lieu d'un « sous conditions » refusé à l'étape suivante.
  slot_start: Type.Optional(Type.String({ format: 'date-time' })),
  email: Type.Optional(Type.String({ format: 'email', maxLength: 255 })),
  // Téléphone saisi (E.164 du tunnel) : reconnaît une fiche SANS email — celle à qui un code
  // personnel a été envoyé par SMS — que l'email seul ne retrouverait jamais.
  phone: Type.Optional(Type.String({ maxLength: 20 })),
  // Lieu choisi, quand il l'est : une promotion peut être limitée à certains lieux (sinon « selon le lieu »).
  pro_place_id: Type.Optional(Type.Integer({ minimum: 1 })),
});
export type CartSummaryRequestObject = Static<typeof CartSummaryRequestSchema>;

/**
 * Finalisation (spec Parcours P3 § 7.6) : conditions de paiement et d'annulation connues AVANT la
 * pré-réservation, pour le bouton unique (« Continuer vers le paiement », « Confirmer le rendez-vous »,
 * « Envoyer ma demande ») et le choix payer en ligne / sur place. Montants et date formatés serveur.
 */
export const CartSummaryCheckoutSchema = Type.Object({
  payment_type: Type.Enum(PrestationPaymentType),
  // Dû en ligne aujourd'hui (acompte, paiement complet ; 0 pour une empreinte, une carte enregistrée, gratuit).
  amount_to_pay_today: Type.String(),
  amount_to_pay_today_raw: Type.Number(),
  remaining_on_site: Type.String(),
  remaining_on_site_raw: Type.Number(),
  // Le pro valide chaque réservation : la finalisation envoie une demande.
  requires_validation: Type.Boolean(),
  // Fin de l'annulation gratuite, absente si elle est déjà passée.
  cancellation_deadline: Type.Optional(Type.String({ format: 'date-time' })),
  cancellation_deadline_formatted: Type.Optional(Type.String()),
});
export type CartSummaryCheckoutObject = Static<typeof CartSummaryCheckoutSchema>;

export const CartSummaryResponseSchema = Type.Object({
  items: Type.Array(CartSummaryItemSchema),
  total_duration: Type.String(),
  total_price: Type.String(),
  voucher_discount: Type.Optional(Type.String()),
  // Remise promo (préfixée « - » comme voucher_discount). Soft : en cart-summary la
  // validation n'est pas ferme — promo_ineligibility_reason porte la raison si non applicable.
  promo_discount: Type.Optional(Type.String()),
  promo_code_label: Type.Optional(Type.String()),
  promo_ineligibility_reason: Type.Optional(Type.Enum(BookingPromoIneligibilityReason)),
  // Phrase destinée au client quand le motif en a une (hors happy hours : les horaires où le code est
  // valable, langue de la requête) — même phrase que `data.user_message` du refus ferme.
  promo_ineligibility_message: Type.Optional(Type.String()),
  // Codes retenus après résolution (pastilles du champ code) : un code promo et une carte cadeau au plus.
  promo_code: Type.Optional(Type.String()),
  voucher_code: Type.Optional(Type.String()),
  // Promotion retenue appliquée sans code (automatique) : pas de pastille de code, une ligne « Remise ».
  promo_is_automatic: Type.Optional(Type.Boolean()),
  // Conditions de la promotion retenue pas encore tranchées (créneau, lieu, client) : remise annoncée,
  // vérifiée à l'étape suivante (« −10 € nouveaux clients : vérifié avec votre email »).
  promo_pending_conditions: Type.Optional(Type.Array(Type.Enum(PromotionPendingCondition))),
  promo_customer_scope: Type.Optional(Type.Enum(ProPromoCodeCustomerScope)),
  amounts: Type.Optional(BookingAmountsSchema),
  // Présent quand le créneau est connu (`slot_start`) : cf. CartSummaryCheckoutSchema.
  checkout: Type.Optional(CartSummaryCheckoutSchema),
});
export type CartSummaryResponseObject = Static<typeof CartSummaryResponseSchema>;

// ─── Booking Tunnel: Validate Zone Address ───

export const ValidateZoneAddressRequestSchema = Type.Object({
  pro_place_id: Type.Number(),
  google_place_id: Type.String({ maxLength: 512 }),
});
export type ValidateZoneAddressRequestObject = Static<typeof ValidateZoneAddressRequestSchema>;

export const ValidateZoneAddressResponseSchema = Type.Object({
  address: AddressSchema,
});
export type ValidateZoneAddressResponseObject = Static<typeof ValidateZoneAddressResponseSchema>;
